// `firebase/app` stand-in. The "app" is just the API base URL plus a shared
// token source; every other module hangs off it.
export interface FirebaseOptions {
  apiKey?: string; authDomain?: string; projectId?: string; storageBucket?: string;
  messagingSenderId?: string; appId?: string; measurementId?: string;
  /** BattleSuite: the API origin, e.g. https://api.battlebooks.ca. Falls back to the *_API_BASE_URL env. */
  apiBaseUrl?: string;
}

export interface FirebaseApp {
  name: string;
  options: FirebaseOptions;
  /** @internal */
  _baseUrl: string;
  /** @internal token provider, installed by auth */
  _getToken?: (forceRefresh?: boolean) => Promise<string | null>;
  /** @internal */
  _bucket: string;
}

const apps: FirebaseApp[] = [];

declare const process: { env: Record<string, string | undefined> } | undefined;

// Written as literal `process.env.X` so Next/Expo/Vite inline them at build time.
function envBase(): string {
  let v = '';
  try {
    if (typeof process !== 'undefined' && process?.env) {
      v = process.env.NEXT_PUBLIC_API_BASE_URL || process.env.EXPO_PUBLIC_API_BASE_URL || '';
    }
  } catch { /* no process */ }
  return v.replace(/\/fn\/?$/, '').replace(/\/$/, '');
}

// The file store has one namespace, `battlebooks-ca`. A leftover Firebase bucket name
// in an app's env (`*.firebasestorage.app`, `*.appspot.com`) must not become a path.
function bucketOf(v?: string): string {
  const b = (v || '').replace(/^gs:\/\//, '');
  return !b || /firebasestorage|appspot/.test(b) ? 'battlebooks-ca' : b;
}

export function initializeApp(options: FirebaseOptions = {}, name = '[DEFAULT]'): FirebaseApp {
  const existing = apps.find((a) => a.name === name);
  if (existing) return existing;
  const base = (options.apiBaseUrl || envBase()).replace(/\/$/, '');
  if (!base && typeof console !== 'undefined') console.warn('[battlesuite] no API base URL configured (NEXT_PUBLIC_API_BASE_URL)');
  const app: FirebaseApp = { name, options, _baseUrl: base, _bucket: bucketOf(options.storageBucket) };
  apps.push(app);
  return app;
}

export function getApps(): FirebaseApp[] { return apps.slice(); }
export function getApp(name = '[DEFAULT]'): FirebaseApp {
  const a = apps.find((x) => x.name === name);
  if (!a) throw new Error(`[battlesuite] app ${name} not initialized`);
  return a;
}
export function deleteApp(app: FirebaseApp): Promise<void> { const i = apps.indexOf(app); if (i >= 0) apps.splice(i, 1); return Promise.resolve(); }

export class FirebaseError extends Error {
  constructor(readonly code: string, message: string, readonly customData?: Record<string, unknown>) { super(message); this.name = 'FirebaseError'; }
}

/** Every request's deadline unless the caller brings its own signal (callables set theirs; uploads use raw fetch). */
export const API_DEADLINE_MS = 30_000;

/**
 * @internal shared fetch with bearer + JSON, used by every module.
 *
 * A deadline on every call (Lj, Oct 17: "BattleBids keeps failing to load things"): a request that stalls on a bad bar
 * of LTE used to hang forever — onSnapshot's poll saw it as still in flight and never read again, and a stalled token
 * refresh pinned every request behind it. The abort also tears down a body read that stalls mid-way. `timeoutMs`
 * overrides the default; an `init.signal` of your own disables it.
 */
export async function apiFetch(app: FirebaseApp, path: string, init: RequestInit & { auth?: boolean; json?: unknown; timeoutMs?: number } = {}): Promise<Response> {
  const { auth, json, timeoutMs, ...rest } = init;
  const headers = new Headers(rest.headers ?? {});
  if (json !== undefined) headers.set('content-type', 'application/json');
  const ctrl = !rest.signal && typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs ?? API_DEADLINE_MS) : null;
  try {
    if (auth !== false && app._getToken) {
      const t = await app._getToken();
      if (t) headers.set('authorization', `Bearer ${t}`);
    }
    return await fetch(`${app._baseUrl}${path}`, { ...rest, signal: rest.signal ?? ctrl?.signal, headers, body: json !== undefined ? JSON.stringify(json) : rest.body });
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') { const err = new Error('The request took too long'); err.name = 'AbortError'; throw err; }
    throw e;
  } finally {
    // the timer stays armed while the caller reads the body, so a stalled body read is cut off too; it is cleared
    // on the next macrotask after the body has had its window
    if (timer) setTimeout(() => clearTimeout(timer), (timeoutMs ?? API_DEADLINE_MS) + 10);
  }
}
